import { Link, useLocation } from '@tanstack/react-router';
import { Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { SidebarTrigger, useSidebar } from '@/components/ui/sidebar';
import { findNavigationItem } from '@/components/layout/nav-config';
import { useAuth } from '@/providers/useAuth';
import { useSetTheme, useTheme } from '@/stores/useUiStore';

export function Header() {
    const { t } = useTranslation();
    const location = useLocation();
    const theme = useTheme();
    const setTheme = useSetTheme();
    const { username } = useAuth();
    const { isMobile, openMobile } = useSidebar();
    const currentNavItem = findNavigationItem(location.pathname);

    function toggleTheme(): void {
        setTheme(theme === 'dark' ? 'light' : 'dark');
    }

    return (
        <header className='sticky top-0 z-20 flex h-16 shrink-0 items-center gap-3 border-b bg-background/95 px-4 backdrop-blur'>
            <SidebarTrigger size='mobileMenu' aria-label={openMobile ? t('common.close') : t('common.toggleSidebar')}>
                <span className='text-sm font-medium'>{openMobile ? t('common.close') : 'Menu'}</span>
            </SidebarTrigger>
            <div className='min-w-0'>
                <Breadcrumb className={isMobile ? 'hidden sm:block' : undefined}>
                    <BreadcrumbList>
                        <BreadcrumbItem>
                            <BreadcrumbLink asChild>
                                <Link to='/home'>{t('nav.home')}</Link>
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        {currentNavItem ? (
                            <>
                                <BreadcrumbSeparator />
                                <BreadcrumbItem>
                                    <BreadcrumbPage>{t(`nav.${currentNavItem.title}`)}</BreadcrumbPage>
                                </BreadcrumbItem>
                            </>
                        ) : null}
                    </BreadcrumbList>
                </Breadcrumb>
            </div>
            <div className='ml-auto flex items-center gap-2'>
                <span className='hidden text-sm text-muted-foreground md:inline'>{username ?? t('common.guest')}</span>
                <Button
                    variant='ghost'
                    size='icon'
                    className='h-8 w-8'
                    onClick={toggleTheme}
                    aria-label={t('common.toggleTheme')}
                >
                    <Sun className='h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0' />
                    <Moon className='absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100' />
                    <span className='sr-only'>{t('common.toggleTheme')}</span>
                </Button>
            </div>
        </header>
    );
}
